import { LitElement, html, css, unsafeHTML } from '@hh/global.js'

class CategoryItem extends LitElement {
  static properties = {
    tag: { type: String },
    text: { type: String },
    imageUrl: { type: String },
    imageAlt: { type: String },
    bgImage: { type: String },
    bgColor: { type: String },
    link: { type: String },
    imageWidth: { type: Number },
    imageHeight: { type: Number },
  }

  constructor() {
    super()
    this.render()
  }

  addReverseAnimation() {
    const circle = this.renderRoot?.querySelector('.js-circle')
    if (!circle) {
      return
    }

    setTimeout(() => {
      circle.classList.add('category-item__circle--loaded')
    }, 800)
  }

  firstUpdated() {
    this.addReverseAnimation()
  }

  render() {
    let containerStyle = ''
    if (this.bgColor) {
      containerStyle += 'background-color: ' + this.bgColor + ';'
    }

    const bgImage = this.bgImage
      ? `<img
          src="${this.bgImage}"
          class="category-item__background"
          loading="lazy"
        >`
      : ''
    const toyImage = this.imageUrl
      ? `<img
          src="${this.imageUrl}"
          class="category-item__image"
          alt="${this.imageAlt}"
          loading="lazy"
          width="${this.imageWidth}"
          height="${this.imageHeight}"
        >`
      : ''

    const template = `<${this.tag}
      class="category-item__container"
      tabindex="-1"
      ${this.link ? 'href="' + this.link + '"' : ''}
      ${containerStyle ? 'style="' + containerStyle + '"' : ''}
    >
      ${bgImage}${toyImage}
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 100 100"
        height="68"
        width="68"
        aria-hidden="true"
        focusable="false"
        role="presentation"
        class="category-item__animatable"
      >
        <circle
          class="category-item__circle js-circle"
          cx="30"
          cy="50"
          r="50"
          stroke="var(--border-color)"
          fill-opacity="0"
          transform="rotate(-90 40 40)"
        />
      </svg>
    </${this.tag}>`
    const titleContent =
      this.tag === 'a' && this.link
        ? `<a href="${this.link} tabindex="0">${this.text}</a>`
        : this.text

    return html`<div class="category-item">
      ${unsafeHTML(template)}
      <p class="category-item__title">${unsafeHTML(titleContent)}</p>
    </div>`
  }

  static styles = css`
    .category-item {
      text-align: center;
      text-transform: uppercase;
    }

    .category-item__container {
      margin: 9px;
      border-radius: 50%;
      display: block;
      padding: 33px;
      position: relative;
      z-index: 1;
    }

    .category-item__background {
      inset: 0;
      max-width: 100%;
      position: absolute;
      z-index: -1;
    }

    .category-item__image {
      left: 50%;
      position: absolute;
      top: 50%;
      transform: translate(-50%, -50%);
      z-index: 0;
    }

    .category-item__animatable {
      inset: -1px;
      overflow: visible;
      position: absolute;
      z-index: -1;
    }

    .category-item__circle {
      animation: strokeEmpty 0.8s linear forwards;
      stroke-dasharray: 360;
      stroke-dashoffset: 360;
      stroke-width: 0;
      transition: stroke-dashoffset 0.8s cubic-bezier(0.5, 1, 0.89, 1);
    }

    .category-item__circle--loaded {
      stroke-width: 4px;
    }

    :host(:hover) .category-item__circle {
      animation: strokeFill 0.8s linear forwards;
    }

    .category-item__title {
      font-size: 1rem;
      letter-spacing: 1px;
      line-height: 1.4;
      margin: 0;
    }

    a {
      color: var(--hh-color-brand-secondary);
      text-decoration: none;
    }

    @media screen and (min-width: 1024px) {
      .category-item__container {
        margin: 12px;
        padding: 44px;
      }

      .category-item__image {
        width: auto;
        height: auto;
      }

      .category-item__animatable {
        height: 90px;
        width: 90px;
      }

      .category-item__title {
        font-size: 1.3rem;
      }
    }

    @keyframes strokeFill {
      to {
        stroke-dashoffset: 0;
      }
    }

    @keyframes strokeEmpty {
      from {
        stroke-dashoffset: 0;
      }

      to {
        stroke-dashoffset: 360;
      }
    }
  `
}

if (!customElements.get('hh-category-item')) {
  customElements.define('hh-category-item', CategoryItem);
}